import { Button, Form, Input, Row } from 'antd';
import type { ButtonProps } from 'antd/lib/button';
import type { FormInstance } from 'antd/lib/form';
import { FC, ReactNode, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import type { DefaultTheme } from 'styled-components';
import { LoadingIndicator } from '../';
import AppContext from './AppContext';

type StringOrElementType = JSX.Element | string | ReactNode;
type ResolveButtonTextType = string | ReactNode | null | undefined;

export interface DeleteModalContentProps {
  intl: IntlShape;
  themeContext: DefaultTheme;
  title: StringOrElementType;
  deleteItemNameTitle?: string;
  deleteItemNameValue?: string;
  extraInfo?: StringOrElementType;
  description: StringOrElementType;
  form: FormInstance;
  showDeleteInputField?: boolean;
  showWarning?: boolean;
  okText?: string;
  cancelText?: string;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  onOkAsync?: () => Promise<void> | undefined;
  onOk?: () => void;
  onCancel?: () => void;
  handleHideConfirm: () => void;
}

const resolveButtonText = (
  text: ResolveButtonTextType,
  intl: IntlShape,
  defaultText: string,
) => {
  if (text == null || typeof text === 'string') {
    return intl.formatMessage({ id: text || defaultText });
  }

  return text;
};

const DeleteModalContent: FC<DeleteModalContentProps> = ({
  intl,
  themeContext,
  title,
  deleteItemNameTitle,
  deleteItemNameValue,
  extraInfo,
  description,
  form,
  showDeleteInputField,
  showWarning,
  okText,
  cancelText,
  onOkAsync,
  onOk,
  onCancel,
  okButtonProps,
  cancelButtonProps,
  handleHideConfirm,
}) => {
  const layout = {
    wrapperCol: {
      span: 14,
    },
  };
  const [loading, setLoading] = useState(false);
  const onClickOk = async () => {
    const finalFn =
      typeof onOkAsync === 'function'
        ? async () => {
            if (showDeleteInputField) {
              if (form) {
                form
                  .validateFields()
                  .then(async () => {
                    setLoading(true);
                    if (onOkAsync) {
                      await onOkAsync();
                    }
                    form.resetFields();
                    handleHideConfirm();
                    setLoading(false);
                  })
                  .catch(() => {
                    form.submit();
                  });
              }
            } else {
              setLoading(true);
              if (onOkAsync) {
                await onOkAsync();
              }
              if (form) {
                form.resetFields();
              }
              handleHideConfirm();
              setLoading(false);
            }
          }
        : () => {
            if (onOk) {
              onOk();
            }
            if (form) {
              form.resetFields();
            }
            handleHideConfirm();
          };

    finalFn();
  };

  const onClickCancel = async () => {
    if (onCancel) {
      onCancel();
    }
    handleHideConfirm();
  };

  return (
    <AppContext intl={intl} themeContext={themeContext}>
      <div className="delete-modal-confirm" data-testid="DeleteModalContent">
        <div className="title">
          {typeof title === 'string' ? (
            <FormattedHTMLMessage id={title} />
          ) : (
            title
          )}
        </div>

        {deleteItemNameTitle && (
          <div className="name">
            <span className="name-title">
              {intl.formatMessage({ id: deleteItemNameTitle })}
            </span>
            <span className="name-value">{deleteItemNameValue}</span>
          </div>
        )}

        {extraInfo && <div className="extra-info">{extraInfo}</div>}
        {description && (
          <div className="description">
            {typeof description === 'string' ? (
              <FormattedHTMLMessage id={description} />
            ) : (
              description
            )}
          </div>
        )}

        {showWarning && showDeleteInputField && (
          <Form
            {...layout}
            form={form}
            onFinish={async () => {
              await onClickOk();
            }}
            name="delete-modal-confirm-form"
            className="delete-modal-confirm-form"
          >
            <Form.Item
              name="deleteInput"
              className="h-58"
              label={
                <b>
                  <FormattedMessage id="Global.message.input.label" />
                </b>
              }
              validateFirst
              initialValue=""
              validateTrigger={['onChange', 'onSubmit']}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'Global.message.input.deleteConfirm',
                  }),
                  validateTrigger: ['onChange', 'onSubmit'],
                },
                {
                  validator: async (_, value: string) => {
                    const enteredText = value || '';
                    const deleteText = 'DELETE';
                    if (
                      enteredText !==
                      deleteText.substring(0, enteredText.length)
                    ) {
                      throw new Error(
                        intl.formatMessage({
                          id: 'Global.message.input.deleteConfirm',
                        }),
                      );
                    }
                  },
                  validateTrigger: ['onChange', 'onSubmit'],
                },
                {
                  enum: ['DELETE'],
                  type: 'enum',
                  message: intl.formatMessage({
                    id: 'Global.message.input.deleteConfirm',
                  }),
                  validateTrigger: ['onSubmit'],
                },
              ]}
            >
              <Input
                data-testid="delete-input"
                allowClear
                size="middle"
                autoComplete="off"
              />
            </Form.Item>
          </Form>
        )}
        {showWarning && (
          <Row justify="center" className="mt-24">
            <Button
              type="default"
              shape="round"
              size="middle"
              className="mw-200 mx-8"
              {...cancelButtonProps}
              data-testid="cancel-btn"
              onClick={onClickCancel}
            >
              {resolveButtonText(cancelText, intl, 'Global.cancel')}
            </Button>
            <Button
              type="primary"
              danger
              shape="round"
              size="middle"
              className="mw-200 mx-8"
              data-testid="delete-btn"
              {...okButtonProps}
              onClick={async () => {
                if (typeof onOkAsync === 'function' && showDeleteInputField) {
                  if (form) {
                    form.submit();
                  }
                } else {
                  await onClickOk();
                }
              }}
            >
              {resolveButtonText(okText, intl, 'Global.delete')}
            </Button>
          </Row>
        )}
        {!showWarning && (
          <Row justify="center" className="mt-24">
            <Button
              type="primary"
              shape="round"
              size="middle"
              className="mw-200 mx-8"
              data-testid="ok-btn"
              {...okButtonProps}
              onClick={onClickOk}
            >
              {resolveButtonText(okText, intl, 'Global.ok')}
            </Button>
          </Row>
        )}
        {loading && <LoadingIndicator spinning>&nbsp;</LoadingIndicator>}
      </div>
    </AppContext>
  );
};

export default DeleteModalContent;
